/**
 * Local calendar bounds for cc-case-stage-process-report.
 * Use explicit local offset ISO strings (not `Date#toISOString()` / UTC Z), otherwise
 * "Today" boundaries shift by timezone (e.g. IST midnight becomes previous UTC day).
 */

function startOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * ISO 8601 with this date's local offset (e.g. +05:30), not UTC Z.
 * Keeps start/end aligned to the user's calendar day for API filters.
 */
function toLocalOffsetISOString(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  const mss = String(date.getMilliseconds()).padStart(3, "0");
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  const oh = String(Math.floor(abs / 60)).padStart(2, "0");
  const om = String(abs % 60).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mi}:${ss}.${mss}${sign}${oh}:${om}`;
}

/**
 * @param {string} timeframeLabel — e.g. "Today", "Weekly", "Monthly" (today back one calendar month),
 *   "Till Now", "All Monthly"
 * @returns {{ startDate: string, endDate: string }}
 */
export function getChartDateRangeForTimeframe(timeframeLabel) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (timeframeLabel) {
    case "Today":
    case "All Today":
      return {
        startDate: toLocalOffsetISOString(startOfLocalDay(today)),
        endDate: toLocalOffsetISOString(endOfLocalDay(today)),
      };

    case "Weekly":
    case "All Weekly": {
      /* Last 7 local calendar days including today (not 8). */
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return {
        startDate: toLocalOffsetISOString(startOfLocalDay(start)),
        endDate: toLocalOffsetISOString(endOfLocalDay(today)),
      };
    }

    case "All Monthly": {
      /* Month-to-date: first day of current month → end of today (local). */
      //const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return {
        startDate: toLocalOffsetISOString(startOfLocalDay(start)),
        endDate: toLocalOffsetISOString(endOfLocalDay(today)),
      };
    }

    case "Monthly": {
      /* Rolling month ending today (local): start = same calendar day one month back, end = today.
         If that day does not exist (e.g. Mar 30 → Feb 30), clamp to the last day of that month
         (e.g. Feb 29 leap / Feb 28). Mirrors Weekly’s “through today” pattern. */
      // const start = new Date(
      //   today.getFullYear(),
      //   today.getMonth(),
      //   today.getDate()
      // );
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      // const dayOfMonth = start.getDate();
      // start.setMonth(start.getMonth() - 1);
      // if (start.getDate() !== dayOfMonth) {
      //   start.setDate(0);
      // }
      return {
        startDate: toLocalOffsetISOString(startOfLocalDay(start)),
        endDate: toLocalOffsetISOString(endOfLocalDay(today)),
      };
    }

    case "Till Now": {
      const start = new Date(today.getFullYear(), 0, 1);
      return {
        startDate: toLocalOffsetISOString(startOfLocalDay(start)),
        endDate: toLocalOffsetISOString(endOfLocalDay(today)),
      };
    }

    default: {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: toLocalOffsetISOString(startOfLocalDay(start)),
        endDate: toLocalOffsetISOString(endOfLocalDay(today)),
      };
    }
  }
}

/**
 * Parse `getChartDateRangeForTimeframe` / API-style offset ISO back to `yyyy-mm-dd` in local calendar.
 * @param {string} iso
 * @returns {string}
 */
export function localYmdFromOffsetISO(iso) {
  if (!iso || typeof iso !== "string") return "";
  const d = new Date(iso.trim());
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Custom calendar range for charts (local days, start 00:00, end 23:59:59.999).
 * Swaps if end is before start. Invalid strings fall back to month-to-date like "All Monthly".
 * @param {string} startYmd - `yyyy-mm-dd`
 * @param {string} endYmd - `yyyy-mm-dd`
 * @returns {{ startDate: string, endDate: string }}
 */
export function getChartDateRangeForYmdStrings(startYmd, endYmd) {
  const parse = (s) => {
    if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (
      dt.getFullYear() !== y ||
      dt.getMonth() !== m - 1 ||
      dt.getDate() !== d
    ) {
      return null;
    }
    return dt;
  };
  let start = parse(startYmd);
  let end = parse(endYmd);
  if (!start || !end) return getChartDateRangeForTimeframe("All Monthly");
  if (start > end) {
    const t = start;
    start = end;
    end = t;
  }
  return {
    startDate: toLocalOffsetISOString(startOfLocalDay(start)),
    endDate: toLocalOffsetISOString(endOfLocalDay(end)),
  };
}
